/**
 * Pure utility to replay entire encounter chart state at a specific point in time.
 * 
 * This is the CANONICAL encounter replay function.
 * All encounter-level chart replay should use this function.
 * 
 * Purpose:
 * - Generate complete chart snapshots at any point in time
 * - Enable timeline scrubber (drag slider → see entire chart state at that moment)
 * - Support audit trail (reconstruct chart state for any historical moment)
 * - Teaching/demo mode (show chart progression step-by-step)
 * - Disaster recovery verification (replay from events to verify chart integrity)
 * 
 * Future usage:
 * - Timeline scrubber: user drags slider → chart updates to show state at that time
 * - Audit review: "Show me the chart state at 2:30 PM"
 * - Teaching mode: step through encounter showing chart at each milestone
 * - Recovery verification: replay events → verify chart matches expected state
 */

import type { ToothEvent } from "../types/domain";
import { replayToothAtTime } from "./replayToothAtTime";

/**
 * Replays entire encounter chart state at a specific point in time.
 * 
 * @param events - Array of ToothEvent objects for the encounter (all teeth)
 * @param upTo - Optional ISO timestamp. If provided, only events with created_at <= upTo
 *               are considered. If omitted, uses all events (latest state).
 * @returns Record mapping toothCode to resolved imageKey
 * 
 * @example
 * // Get current chart state (latest)
 * const currentChart = replayEncounterChartAtTime(allEvents);
 * // { "UR6": "rct_open", "LL1": "crown", "LR3": "filling", ... }
 * 
 * // Get chart state at specific time
 * const chartAt2pm = replayEncounterChartAtTime(allEvents, "2026-01-28T14:00:00Z");
 * // { "UR6": "normal", "LL1": "filling", ... }
 */
export function replayEncounterChartAtTime(
  events: ToothEvent[],
  upTo?: string
): Record<string, string> {
  if (!events || events.length === 0) {
    return {};
  }

  // Group events by tooth_code (with legacy fallback to 'tooth' field)
  const grouped: Record<string, ToothEvent[]> = {};

  for (const event of events) {
    // Skip undo events (they don't contribute to visual state)
    const payload = event.payload as { undo?: boolean } | null;
    if (payload?.undo === true) {
      continue;
    }

    // Extract tooth code (prefer tooth_code, fallback to legacy 'tooth' field)
    const toothCode =
      event.tooth_code ||
      ((event as { tooth?: string }).tooth as string | undefined);

    if (!toothCode) {
      continue; // Skip events without tooth identifier
    }

    if (!grouped[toothCode]) {
      grouped[toothCode] = [];
    }

    grouped[toothCode].push(event);
  }

  // Build chart state: resolve imageKey for each tooth
  const chartState: Record<string, string> = {};

  for (const toothCode in grouped) {
    const toothEvents = grouped[toothCode];

    // Resolve visual state for this tooth at the specified time
    // replayToothAtTime handles time filtering and precedence internally
    const imageKey = replayToothAtTime(toothEvents, upTo);

    // Only include teeth that have resolved to a non-default state
    // (or include all if you want to show "normal" teeth too)
    // For now, include all teeth that have events
    chartState[toothCode] = imageKey;
  }

  return chartState;
}
